import React from 'react';
import type { BodyIssueCheckInRecord, BodyIssueFeedbackNoteRecord } from '../../types/bodyMapIssue';

function formatTimestamp(value?: unknown) {
  if (!value) return 'Date not set';
  const date = (value as { toDate?: () => Date }).toDate?.() ?? new Date(value as string);
  if (Number.isNaN(date.getTime())) return 'Date not set';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function IssueTrendPanel({
  checkIns,
  feedbackNotes,
}: {
  checkIns: BodyIssueCheckInRecord[];
  feedbackNotes: BodyIssueFeedbackNoteRecord[];
}) {
  const timeline = [
    ...checkIns.map((checkIn) => ({
      id: `checkin-${checkIn.id}`,
      type: 'check_in' as const,
      createdAt: checkIn.createdAt || checkIn.recordedAt,
      payload: checkIn,
    })),
    ...feedbackNotes.map((note) => ({
      id: `feedback-${note.id}`,
      type: 'feedback' as const,
      createdAt: note.createdAt,
      payload: note,
    })),
  ].sort((left, right) => {
    const leftDate = ((left.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date(left.createdAt as string)).getTime();
    const rightDate = ((right.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date(right.createdAt as string)).getTime();
    return rightDate - leftDate;
  });

  return (
    <section className="rounded-3xl border p-6 md:p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
      <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--melio-sage)' }}>
        Timeline
      </p>
      <h2 className="text-2xl font-normal mb-5" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
        Check-ins over time
      </h2>

      {timeline.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
          No check-ins or clinician notes yet for this issue.
        </p>
      ) : (
        <div className="space-y-3">
          {timeline.map((entry) => entry.type === 'check_in' ? (
            <div key={entry.id} className="rounded-2xl border px-4 py-4" style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)' }}>
              <div className="flex items-start justify-between gap-4 mb-2">
                <div style={{ color: 'var(--melio-charcoal)', fontWeight: 600 }}>
                  {entry.payload.betterSameWorse.replace(/_/g, ' ')}
                </div>
                <div className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                  {formatTimestamp(entry.payload.createdAt || entry.payload.recordedAt)}
                </div>
              </div>
              <div className="text-sm mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                Pain {entry.payload.painScore ?? '—'}/10 • {entry.payload.functionImpact} impact • {entry.payload.visibleChange.replace(/_/g, ' ')}
              </div>
              {entry.payload.symptomTypes.length > 0 && (
                <div className="text-sm mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                  {entry.payload.symptomTypes.map((item) => item.replace(/_/g, ' ')).join(', ')}
                </div>
              )}
              {entry.payload.freeText && (
                <div className="text-sm" style={{ color: 'var(--melio-charcoal)' }}>
                  {entry.payload.freeText}
                </div>
              )}
              {entry.payload.redFlagTriggered && (
                <div className="mt-3 text-sm" style={{ color: '#b91c1c' }}>
                  Review flag: {entry.payload.redFlagReason || 'Potential escalation signal'}
                </div>
              )}
            </div>
          ) : (
            <div key={entry.id} className="rounded-2xl border px-4 py-4" style={{ borderColor: 'rgba(79, 93, 24, 0.25)', backgroundColor: 'rgba(79, 93, 24, 0.08)' }}>
              <div className="flex items-start justify-between gap-4 mb-2">
                <div style={{ color: 'var(--melio-charcoal)', fontWeight: 600 }}>
                  Clinician / ops note
                </div>
                <div className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                  {formatTimestamp(entry.payload.createdAt)}
                </div>
              </div>
              <div className="text-sm mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                {entry.payload.authorRole}{entry.payload.authorName ? ` • ${entry.payload.authorName}` : ''}
              </div>
              <div className="text-sm" style={{ color: 'var(--melio-charcoal)' }}>
                {entry.payload.note}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
